import { Ban, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { EmailReviewStatus } from '@/types/messaging';

const BADGES = {
  pending_review: { key: 'pending', className: 'text-amber-700 border-amber-200 bg-amber-50', Icon: Clock },
  connected: { key: 'connected', className: 'text-green-700 border-green-200 bg-green-50', Icon: CheckCircle2 },
  rejected: { key: 'rejected', className: 'text-muted-foreground', Icon: Ban },
} as const satisfies Record<EmailReviewStatus, { key: string; className: string; Icon: typeof Clock }>;

/** Whether the client can see an imported email conversation: in review, shown, or not shared. */
export function EmailReviewBadge({ status }: { status: EmailReviewStatus }) {
  const t = useTranslations('consultantMessages.page.emailReview');
  const { key, className, Icon } = BADGES[status];
  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-1.5 py-0 h-4 font-medium gap-0.5 shrink-0 ${className}`}
      title={t(`badge.${key}.title`)}
      data-testid="email-review-badge"
      data-status={status}
    >
      <Icon className="h-2.5 w-2.5" />
      {t(`badge.${key}.label`)}
    </Badge>
  );
}

interface EmailReviewBannerProps {
  status: EmailReviewStatus;
  acting: boolean;
  onApprove: () => void;
  onReject: () => void;
}

/** Under the thread header: what the reviewer must decide, or why the client can't see it. */
export function EmailReviewBanner({ status, acting, onApprove, onReject }: EmailReviewBannerProps) {
  const t = useTranslations('consultantMessages.page.emailReview');
  if (status === 'connected') return null;

  if (status === 'rejected') {
    return (
      <div className="px-4 py-2 border-b bg-muted/40 text-xs text-muted-foreground shrink-0" data-testid="email-review-banner">
        {t('bannerRejected')}
      </div>
    );
  }

  return (
    <div
      className="px-4 py-2 border-b bg-amber-50 flex items-center justify-between gap-3 shrink-0"
      data-testid="email-review-banner"
    >
      <p className="text-xs text-amber-800">{t('bannerPending')}</p>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onReject} disabled={acting}>
          {t('reject')}
        </Button>
        <Button size="sm" onClick={onApprove} disabled={acting}>
          {acting && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {t('approve')}
        </Button>
      </div>
    </div>
  );
}
